import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import Select from 'react-select'
import useSWR from 'swr'
import { api, updateTournamentSettings, updateSeason, createSeasonsBulk, deleteSeason, deleteChildCompetition, updateSeasonLicense, bulkUpdateSeasonLicense, uploadSeasonBackground, deleteSeasonBackground } from '../services/api'
import { fetchImages } from '../services/api'
import type { TournamentSettingsChild } from '../services/api'
import { Badge, Banner, Button, Modal, ComplexSelect, OverridableField, CompetitionsEditor, ImageSlot, ImageManagement, SlugManager, useSpeedbump } from '../components'
import {
    SEASON_BACKGROUND_REQUIRED_HEIGHT,
    SEASON_BACKGROUND_REQUIRED_WIDTH,
} from '../constants/seasonBackground'
import type { Complex as ComplexSelectType, CompetitionItem } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import formStyles from '../styles/forms.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './TournamentDetail.module.scss'

interface CountryOption {
    value: string
    label: string
}

interface TimezoneOption {
    value: string
    label: string
}

const TIMEZONE_OPTIONS: TimezoneOption[] = (
    typeof Intl !== 'undefined' && typeof (Intl as any).supportedValuesOf === 'function'
        ? ((Intl as any).supportedValuesOf('timeZone') as string[])
        : []
).map(tz => ({ value: tz, label: tz }))

interface Complex {
    tc_complex_id: string
    ext_complex_id: string | null
    tc_name: string | null
    ext_name: string | null
}

interface SeasonBackgroundImage {
    tc_image_id: string
    cdn_url: string
    thumbnail_url?: string
    pixel_width?: number
    pixel_height?: number
    aspect_ratio?: string
    mime_type?: string
    created_at?: string
    created_by?: string | null
}

interface ManagedImage {
    image_id: string
    type_code: string
    url: string | null
}

interface Season {
    tc_season_id: string
    ext_season_id: string
    tc_name: string | null
    ext_name: string | null
    tc_start_date: string | null
    ext_start_date: string | null
    tc_end_date: string | null
    ext_end_date: string | null
    tc_year: number | null
    ext_year: number | null
    tc_complex_id: string | null
    ext_complex_id: string | null
    tc_complex: Complex | null  // Override complex (from tc_complex_id)
    ext_complex: Complex | null // External complex (from ext_complex_id)
    background_image: SeasonBackgroundImage | null
    is_licensed: boolean
    created_at: string | null
    updated_at: string | null
}

interface ChildCompetition {
    tc_competition_id: string
    ext_competition_id: string
    tc_name: string | null
    ext_name: string | null
    short_name: string | null
    derived_short_name: string | null
    tc_type: string | null
    ext_type: string | null
    tc_gender: string | null
    ext_gender: string | null
    category: string | null
    created_at: string | null
    updated_at: string | null
    seasons: Season[]
}

interface CountryRef {
    code: string
    name: string | null
}

interface ParentCompetition {
    tc_competition_parent_id: string
    ext_competition_parent_id: string
    tc_name: string | null
    ext_name: string | null
    tc_official_name: string | null
    ext_official_name: string | null
    tc_latest_city: string | null
    ext_latest_city: string | null
    tc_latest_country: CountryRef | null
    ext_latest_country: CountryRef | null
    tc_latest_timezone: string | null
    ext_latest_timezone: string | null
    sport_name: string | null
    catalog_source_name: string | null
    created_at: string | null
    updated_at: string | null
}

interface CompetitionDetailsResponse {
    parent_competition: ParentCompetition
    child_competitions: ChildCompetition[]
    summary: {
        total_child_competitions: number
        total_seasons: number
    }
    timestamp: string
}

// Edition = all seasons for a given year across child competitions
interface Edition {
    year: number
    competitions: Array<{
        competition: ChildCompetition
        season: Season | null
    }>
}

// Track changes per season
interface SeasonEdits {
    name: string
    start: string
    end: string
    complex: {
        tc_id: string
        ext_id: string | null
        tc_name: string | null
        ext_name: string | null
    } | null
}


// Helper functions (defined outside component to avoid hoisting issues)
const formatDateForInput = (dateString: string | null): string => {
    if (!dateString) return ''
    try {
        return dateString.split('T')[0]
    } catch {
        return ''
    }
}

const getDisplayName = (tcName: string | null, extName: string | null): string => {
    return tcName || extName || 'Unknown'
}

const getParentDisplayName = (parent: ParentCompetition): string => {
    return (
        parent.tc_official_name ||
        parent.ext_official_name ||
        parent.tc_name ||
        parent.ext_name ||
        'Unknown'
    )
}

const getParentSecondaryName = (parent: ParentCompetition): string => {
    return parent.tc_name || parent.ext_name || ''
}

const getCompetitionLabel = (comp: ChildCompetition): string => {
    return comp.short_name || comp.derived_short_name || getDisplayName(comp.tc_name, comp.ext_name)
}

const TournamentDetail: React.FC = () => {
    const { tournamentId } = useParams<{ tournamentId: string }>()
    const confirm = useSpeedbump()
    const [parentName, setParentName] = useState('')
    const [parentOfficialName, setParentOfficialName] = useState('')
    const [parentLatestCity, setParentLatestCity] = useState('')
    const [parentLatestCountryCode, setParentLatestCountryCode] = useState('')
    const [parentLatestTimezone, setParentLatestTimezone] = useState('')

    // Track all season date edits (keyed by season ID)
    const [seasonEdits, setSeasonEdits] = useState<Record<string, SeasonEdits>>({})
    const [savingSeasons, setSavingSeasons] = useState<Set<string>>(new Set())

    // Track all competition edits as CompetitionItem[] (existing + new in one array)
    const [competitionItems, setCompetitionItems] = useState<CompetitionItem[]>([])
    const [savingAllCompetitions, setSavingAllCompetitions] = useState(false)

    // Create edition state
    const [creatingEdition, setCreatingEdition] = useState(false)

    // Track individual season creation (keyed by "competitionId-year")
    const [addingSeasons, setAddingSeasons] = useState<Set<string>>(new Set())

    // Action-level error state (for user-initiated actions like save, create, etc.)
    const [actionError, setActionError] = useState<string | null>(null)

    // Settings modal state
    const [showSettings, setShowSettings] = useState(false)
    const [showTournamentImages, setShowTournamentImages] = useState(false)
    const [tournamentLogoLoadFailed, setTournamentLogoLoadFailed] = useState(false)

    // License update state
    const [updatingLicense, setUpdatingLicense] = useState<Set<string>>(new Set())

    const { data, isLoading, error, mutate } = useSWR(
        tournamentId ? ['competition-details', tournamentId] : null,
        () => api.get('/competitions/details', { parent_id: tournamentId }),
        { revalidateOnFocus: false }
    )

    const { data: tournamentImagesData, mutate: mutateTournamentImages } = useSWR(
        tournamentId ? ['tournament-images', tournamentId] : null,
        () => fetchImages('tournament', tournamentId),
        { revalidateOnFocus: false }
    )

    const { data: countriesData } = useSWR<{ countries: CountryOption[] }>(
        'countries',
        () => api.get('/publishing/countries'),
        { revalidateOnFocus: false }
    )
    const countryOptions: CountryOption[] = countriesData?.countries || []

    const details: CompetitionDetailsResponse | null = data?.body ? JSON.parse(data.body) : data

    // Initialize season edits when data loads
    useEffect(() => {
        if (details) {
            // Initialize parent name
            const parent = details.parent_competition
            setParentName(parent.tc_name || parent.ext_name || '')
            setParentOfficialName(parent.tc_official_name || parent.ext_official_name || '')
            setParentLatestCity(parent.tc_latest_city || parent.ext_latest_city || '')
            setParentLatestCountryCode(parent.tc_latest_country?.code || parent.ext_latest_country?.code || '')
            setParentLatestTimezone(parent.tc_latest_timezone || parent.ext_latest_timezone || '')

            const initialEdits: Record<string, SeasonEdits> = {}
            details.child_competitions.forEach(comp => {
                comp.seasons.forEach(season => {
                    // Use tc_complex (override) if exists, otherwise ext_complex (external default)
                    const currentComplex = season.tc_complex || season.ext_complex
                    initialEdits[season.tc_season_id] = {
                        name: season.tc_name || season.ext_name || '',
                        start: formatDateForInput(season.tc_start_date || season.ext_start_date),
                        end: formatDateForInput(season.tc_end_date || season.ext_end_date),
                        complex: currentComplex ? {
                            tc_id: currentComplex.tc_complex_id,
                            ext_id: currentComplex.ext_complex_id,
                            tc_name: currentComplex.tc_name,
                            ext_name: currentComplex.ext_name
                        } : null
                    }
                })
            })
            setSeasonEdits(initialEdits)

            // Initialize competition items from existing competitions
            const items: CompetitionItem[] = details.child_competitions.map(comp => ({
                id: comp.tc_competition_id,
                draft: {
                    name: comp.tc_name || comp.ext_name || '',
                    shortName: comp.short_name || comp.derived_short_name || '',
                    type: comp.tc_type || comp.ext_type || '',
                    gender: comp.tc_gender || comp.ext_gender || '',
                    category: comp.category || ''
                },
                external: {
                    name: comp.ext_name,
                    shortName: comp.derived_short_name,
                    type: comp.ext_type,
                    gender: comp.ext_gender,
                    category: comp.category  // category doesn't have ext_ prefix in the API
                },
                canDelete: !comp.ext_competition_id && comp.seasons.length === 0
            }))
            setCompetitionItems(items)
        }
    }, [details])

    // Group seasons by year into "Editions"
    const editions = useMemo(() => {
        if (!details) return []

        const yearMap = new Map<number, Edition>()

        // First, collect all unique years
        const allYears = new Set<number>()
        details.child_competitions.forEach(comp => {
            comp.seasons.forEach(season => {
                const year = season.tc_year || season.ext_year
                if (year) allYears.add(year)
            })
        })

        // Build editions for each year
        allYears.forEach(year => {
            const competitions = details.child_competitions.map(comp => {
                const season = comp.seasons.find(s => (s.tc_year || s.ext_year) === year) || null
                return { competition: comp, season }
            })
            yearMap.set(year, { year, competitions })
        })

        // Sort by year descending
        return Array.from(yearMap.values()).sort((a, b) => b.year - a.year)
    }, [details])

    const configuredTournamentImageCount = useMemo(() => {
        const tournamentImages: ManagedImage[] = tournamentImagesData?.images ?? []
        const supportedTypes = new Set(['competition_logo', 'competition_banner'])
        const configuredTypes = new Set(
            tournamentImages
                .filter(image => supportedTypes.has(image.type_code))
                .map(image => image.type_code)
        )
        return configuredTypes.size
    }, [tournamentImagesData?.images])

    const tournamentLogoUrl = useMemo(() => {
        const tournamentImages: ManagedImage[] = tournamentImagesData?.images ?? []
        const tournamentLogo = tournamentImages.find(image => image.type_code === 'competition_logo')
        return tournamentLogo?.url || null
    }, [tournamentImagesData?.images])

    useEffect(() => {
        setTournamentLogoLoadFailed(false)
    }, [tournamentLogoUrl])

    // Check if a season has unsaved changes
    const hasSeasonChanges = useCallback((season: Season) => {
        const edits = seasonEdits[season.tc_season_id]
        if (!edits) return false

        const originalName = season.tc_name || season.ext_name || ''
        const originalStart = formatDateForInput(season.tc_start_date || season.ext_start_date)
        const originalEnd = formatDateForInput(season.tc_end_date || season.ext_end_date)
        // Compare against resolved complex (tc_complex if exists, otherwise ext_complex)
        const currentComplex = season.tc_complex || season.ext_complex
        const originalComplexId = currentComplex?.tc_complex_id || null
        const editedComplexId = edits.complex?.tc_id || null

        return edits.name !== originalName ||
            edits.start !== originalStart ||
            edits.end !== originalEnd ||
            editedComplexId !== originalComplexId
    }, [seasonEdits])

    // Check if any season in an edition has changes
    const hasEditionChanges = useCallback((edition: Edition) => {
        return edition.competitions.some(({ season }) => season && hasSeasonChanges(season))
    }, [hasSeasonChanges])

    // Check if any edition has changes (for potential future use)
    const _hasAnyChanges = useMemo(() => {
        return editions.some(edition => hasEditionChanges(edition))
    }, [editions, hasEditionChanges])
    void _hasAnyChanges // Suppress unused warning

    // Season name editing - update local state
    const handleSeasonNameChange = useCallback((seasonId: string, value: string) => {
        setSeasonEdits(prev => ({
            ...prev,
            [seasonId]: {
                ...prev[seasonId],
                name: value
            }
        }))
    }, [])

    // Season date editing - update local state
    const handleSeasonDateChange = useCallback((seasonId: string, field: 'start' | 'end', value: string) => {
        setSeasonEdits(prev => ({
            ...prev,
            [seasonId]: {
                ...prev[seasonId],
                [field]: value
            }
        }))
    }, [])

    // Season complex editing - update local state
    const handleSeasonComplexChange = useCallback((seasonId: string, complex: ComplexSelectType | null) => {
        setSeasonEdits(prev => ({
            ...prev,
            [seasonId]: {
                ...prev[seasonId],
                complex: complex
            }
        }))
    }, [])

    // Revert complex to external default value (clears the override)
    const handleRevertComplex = useCallback((season: Season) => {
        setSeasonEdits(prev => ({
            ...prev,
            [season.tc_season_id]: {
                ...prev[season.tc_season_id],
                complex: season.ext_complex ? {
                    tc_id: season.ext_complex.tc_complex_id,
                    ext_id: season.ext_complex.ext_complex_id,
                    tc_name: season.ext_complex.tc_name,
                    ext_name: season.ext_complex.ext_name
                } : null
            }
        }))
    }, [])

    // Revert season name to external value
    const handleRevertSeasonName = useCallback((season: Season) => {
        setSeasonEdits(prev => ({
            ...prev,
            [season.tc_season_id]: {
                ...prev[season.tc_season_id],
                name: season.ext_name || ''
            }
        }))
    }, [])

    // Revert start date to external value
    const handleRevertStartDate = useCallback((season: Season) => {
        setSeasonEdits(prev => ({
            ...prev,
            [season.tc_season_id]: {
                ...prev[season.tc_season_id],
                start: formatDateForInput(season.ext_start_date)
            }
        }))
    }, [])

    // Revert end date to external value
    const handleRevertEndDate = useCallback((season: Season) => {
        setSeasonEdits(prev => ({
            ...prev,
            [season.tc_season_id]: {
                ...prev[season.tc_season_id],
                end: formatDateForInput(season.ext_end_date)
            }
        }))
    }, [])

    // Save a single season
    const handleSaveSeason = useCallback(async (season: Season) => {
        const edits = seasonEdits[season.tc_season_id]
        if (!edits) return

        setSavingSeasons(prev => new Set(prev).add(season.tc_season_id))
        setActionError(null)
        try {
            await updateSeason(season.tc_season_id, {
                tc_name: edits.name || undefined,
                tc_start_date: edits.start || null,
                tc_end_date: edits.end || null,
                tc_complex_id: edits.complex?.tc_id || null
            })
            await mutate()
        } catch (err) {
            console.error('Failed to update season:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to save season: ${message}`)
        } finally {
            setSavingSeasons(prev => {
                const next = new Set(prev)
                next.delete(season.tc_season_id)
                return next
            })
        }
    }, [seasonEdits, mutate])

    // Revert a single season to saved server state
    const handleRevertSeason = useCallback((season: Season) => {
        const currentComplex = season.tc_complex || season.ext_complex
        setSeasonEdits(prev => ({
            ...prev,
            [season.tc_season_id]: {
                name: season.tc_name || season.ext_name || '',
                start: formatDateForInput(season.tc_start_date || season.ext_start_date),
                end: formatDateForInput(season.tc_end_date || season.ext_end_date),
                complex: currentComplex ? {
                    tc_id: currentComplex.tc_complex_id,
                    ext_id: currentComplex.ext_complex_id,
                    tc_name: currentComplex.tc_name,
                    ext_name: currentComplex.ext_name
                } : null
            }
        }))
    }, [])

    // Save all changed seasons in an edition
    const handleSaveEdition = useCallback(async (edition: Edition) => {
        const changedSeasons = edition.competitions
            .filter(({ season }) => season && hasSeasonChanges(season))
            .map(({ season }) => season!)

        for (const season of changedSeasons) {
            await handleSaveSeason(season)
        }
    }, [hasSeasonChanges, handleSaveSeason])

    // Revert all changes in an edition
    const handleRevertEdition = useCallback((edition: Edition) => {
        edition.competitions.forEach(({ season }) => {
            if (season) {
                handleRevertSeason(season)
            }
        })
    }, [handleRevertSeason])

    // Check if a competition item has changes from its original server state
    const hasCompetitionChanges = useCallback((item: CompetitionItem) => {
        if (!item.id) return true // New items are always "changed"
        if (!details) return false
        const original = details.child_competitions.find(c => c.tc_competition_id === item.id)
        if (!original) return true
        return item.draft.name !== (original.tc_name || original.ext_name || '') ||
            item.draft.shortName !== (original.short_name || original.derived_short_name || '') ||
            item.draft.type !== (original.tc_type || original.ext_type || '') ||
            item.draft.gender !== (original.tc_gender || original.ext_gender || '') ||
            item.draft.category !== (original.category || '')
    }, [details])

    // Check if any settings have changes (parent name, official name, location, competitions)
    const hasAnySettingsChanges = useMemo(() => {
        if (!details) return false
        const parent = details.parent_competition
        const originalParentName = parent.tc_name || parent.ext_name || ''
        const originalOfficialName = parent.tc_official_name || parent.ext_official_name || ''
        const originalCity = parent.tc_latest_city || parent.ext_latest_city || ''
        const originalCountryCode = parent.tc_latest_country?.code || parent.ext_latest_country?.code || ''
        const originalTimezone = parent.tc_latest_timezone || parent.ext_latest_timezone || ''
        const hasParentNameChange = parentName !== originalParentName
        const hasOfficialNameChange = parentOfficialName !== originalOfficialName
        const hasCityChange = parentLatestCity !== originalCity
        const hasCountryChange = parentLatestCountryCode !== originalCountryCode
        const hasTimezoneChange = parentLatestTimezone !== originalTimezone
        return (
            hasParentNameChange
            || hasOfficialNameChange
            || hasCityChange
            || hasCountryChange
            || hasTimezoneChange
            || competitionItems.some(item => hasCompetitionChanges(item))
        )
    }, [details, parentName, parentOfficialName, parentLatestCity, parentLatestCountryCode, parentLatestTimezone, competitionItems, hasCompetitionChanges])


    // Save all settings with single bulk API call
    const handleSaveAllSettings = useCallback(async () => {
        if (!details || !tournamentId) return

        setSavingAllCompetitions(true)
        setActionError(null)
        try {
            // Build children array from competitionItems
            const children: TournamentSettingsChild[] = competitionItems
                .filter(item => hasCompetitionChanges(item) && item.draft.name) // Only changed items with names
                .map(item => ({
                    tc_competition_id: item.id, // undefined for new = create
                    tc_name: item.draft.name || undefined,
                    short_name: item.draft.shortName || undefined,
                    tc_type: item.draft.type || undefined,
                    tc_gender: item.draft.gender || undefined,
                    category: item.draft.category || undefined
                }))

            // Only persist tc_* override fields when they differ from the
            // external value; otherwise send NULL so has_tc_override_* = false.
            const parent = details.parent_competition
            const overrideOrNull = (current: string, ext: string | null) => {
                const trimmedCurrent = current.trim()
                const trimmedExt = (ext || '').trim()
                return trimmedCurrent && trimmedCurrent !== trimmedExt ? trimmedCurrent : null
            }

            await updateTournamentSettings(tournamentId, {
                tc_name: parentName,
                tc_official_name: overrideOrNull(parentOfficialName, parent.ext_official_name),
                tc_latest_city: overrideOrNull(parentLatestCity, parent.ext_latest_city),
                tc_latest_country_code: overrideOrNull(parentLatestCountryCode, parent.ext_latest_country?.code ?? null),
                tc_latest_timezone: overrideOrNull(parentLatestTimezone, parent.ext_latest_timezone),
                children
            })

            await mutate()
        } catch (err) {
            console.error('Failed to save settings:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to save settings: ${message}`)
        } finally {
            setSavingAllCompetitions(false)
        }
    }, [details, tournamentId, parentName, parentOfficialName, parentLatestCity, parentLatestCountryCode, parentLatestTimezone, competitionItems, hasCompetitionChanges, mutate])

    // Create new edition - auto-determine year and create seasons for all competitions
    const handleCreateEdition = useCallback(async () => {
        if (!details || !tournamentId) return
        if (details.child_competitions.length === 0) return

        // Clear any previous error
        setActionError(null)

        // Auto-determine year: current year if no edition exists for it, otherwise next year
        const currentYear = new Date().getFullYear()
        const existingYears = new Set(editions.map(e => e.year))
        const newYear = existingYears.has(currentYear) ? currentYear + 1 : currentYear

        setCreatingEdition(true)
        try {
            const response = await createSeasonsBulk({
                parent_id: tournamentId,
                year: newYear,
                seasons: details.child_competitions.map(comp => ({
                    tc_competition_id: comp.tc_competition_id,
                    // Default season name: "{competition name} {year}"
                    tc_name: `${comp.tc_name || comp.ext_name || 'Season'} ${newYear}`
                    // No dates - user will fill in inline
                }))
            })

            // Check for errors in the response (API returns errors array for partial failures)
            if (response?.errors && response.errors.length > 0) {
                setActionError(`Failed to create some seasons: ${response.errors.join(', ')}`)
            }

            await mutate()
        } catch (err) {
            console.error('Failed to create edition:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to create edition: ${message}`)
        } finally {
            setCreatingEdition(false)
        }
    }, [details, tournamentId, editions, mutate])

    // Delete a season (only if no matches)
    const handleDeleteSeason = useCallback(async (seasonId: string, seasonName: string) => {
        if (!window.confirm(`Delete season "${seasonName}"?\n\nThis cannot be undone.`)) return

        setActionError(null)
        try {
            await deleteSeason(seasonId)
            await mutate()
        } catch (err) {
            console.error('Failed to delete season:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to delete season: ${message}`)
        }
    }, [mutate])

    // Delete a competition (only if no seasons)
    const handleDeleteCompetition = useCallback(async (competitionId: string, competitionName: string) => {
        if (!window.confirm(`Delete competition "${competitionName}"?\n\nThis cannot be undone.`)) return

        setActionError(null)
        try {
            await deleteChildCompetition(competitionId)
            await mutate()
        } catch (err) {
            console.error('Failed to delete competition:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to delete competition: ${message}`)
        }
    }, [mutate])

    // Add a season for a competition in an existing edition
    const handleAddSeasonForCompetition = useCallback(async (competition: ChildCompetition, year: number) => {
        if (!tournamentId) return

        const key = `${competition.tc_competition_id}-${year}`
        setAddingSeasons(prev => new Set(prev).add(key))
        setActionError(null)

        try {
            const seasonName = `${competition.tc_name || competition.ext_name || 'Season'} ${year}`
            await createSeasonsBulk({
                parent_id: tournamentId,
                year,
                seasons: [{
                    tc_competition_id: competition.tc_competition_id,
                    tc_name: seasonName
                }]
            })
            await mutate()
        } catch (err) {
            console.error('Failed to add season:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to add season: ${message}`)
        } finally {
            setAddingSeasons(prev => {
                const next = new Set(prev)
                next.delete(key)
                return next
            })
        }
    }, [tournamentId, mutate])

    // Toggle license for a single season
    const handleToggleSeasonLicense = useCallback(async (season: Season) => {
        setUpdatingLicense(prev => new Set(prev).add(season.tc_season_id))
        setActionError(null)

        try {
            await updateSeasonLicense(season.tc_season_id, !season.is_licensed)
            await mutate()
        } catch (err) {
            console.error('Failed to update license:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to update license: ${message}`)
        } finally {
            setUpdatingLicense(prev => {
                const next = new Set(prev)
                next.delete(season.tc_season_id)
                return next
            })
        }
    }, [mutate])

    // Bulk toggle license for all seasons in an edition
    const handleToggleEditionLicense = useCallback(async (edition: Edition, newLicensed: boolean) => {
        const seasonIds = edition.competitions
            .filter(({ season }) => season !== null)
            .map(({ season }) => season!.tc_season_id)

        if (seasonIds.length === 0) return

        // Mark all as updating
        setUpdatingLicense(prev => new Set([...prev, ...seasonIds]))
        setActionError(null)

        try {
            await bulkUpdateSeasonLicense({
                is_licensed: newLicensed,
                season_ids: seasonIds
            })
            await mutate()
        } catch (err) {
            console.error('Failed to update licenses:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to update licenses: ${message}`)
        } finally {
            setUpdatingLicense(prev => {
                const next = new Set(prev)
                seasonIds.forEach(id => next.delete(id))
                return next
            })
        }
    }, [mutate])

    // Check if all seasons in an edition are licensed
    const isEditionFullyLicensed = useCallback((edition: Edition): boolean => {
        const seasonsInEdition = edition.competitions.filter(({ season }) => season !== null)
        if (seasonsInEdition.length === 0) return false
        return seasonsInEdition.every(({ season }) => season!.is_licensed)
    }, [])

    // Check if any seasons in an edition are licensed
    const isEditionPartiallyLicensed = useCallback((edition: Edition): boolean => {
        const seasonsInEdition = edition.competitions.filter(({ season }) => season !== null)
        if (seasonsInEdition.length === 0) return false
        const licensedCount = seasonsInEdition.filter(({ season }) => season!.is_licensed).length
        return licensedCount > 0 && licensedCount < seasonsInEdition.length
    }, [])

    // Read image dimensions client-side to enforce the exact season
    // background resolution before upload.
    const readImageDimensions = useCallback(
        (file: File): Promise<{ width: number; height: number }> =>
            new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file)
                const img = new window.Image()
                img.onload = () => {
                    resolve({ width: img.naturalWidth, height: img.naturalHeight })
                    URL.revokeObjectURL(url)
                }
                img.onerror = () => {
                    reject(new Error('Unable to read image dimensions'))
                    URL.revokeObjectURL(url)
                }
                img.src = url
            }),
        [],
    )

    const handleUploadSeasonBackground = useCallback(async (season: Season, file: File) => {
        const { width, height } = await readImageDimensions(file)
        if (width !== SEASON_BACKGROUND_REQUIRED_WIDTH || height !== SEASON_BACKGROUND_REQUIRED_HEIGHT) {
            throw new Error(
                `Image must be exactly ${SEASON_BACKGROUND_REQUIRED_WIDTH} × ${SEASON_BACKGROUND_REQUIRED_HEIGHT}. ` +
                `Uploaded image is ${width} × ${height}.`,
            )
        }
        await uploadSeasonBackground(season.tc_season_id, file)
        await mutate()
    }, [mutate, readImageDimensions])

    const handleDeleteSeasonBackground = useCallback(async (season: Season) => {
        const seasonLabel = season.tc_name || season.ext_name || 'this season'
        const ok = await confirm({
            title: 'Delete background image?',
            message: `Delete the background image for ${seasonLabel}?`,
            confirmLabel: 'Delete',
            confirmVariant: 'danger',
        })
        if (!ok) return
        await deleteSeasonBackground(season.tc_season_id)
        await mutate()
    }, [mutate, confirm])

    if (error) {
        return (
            <div className={pageStyles.pageContainer}>
                <div className={pageStyles.error}>
                    <h2>Error loading tournament</h2>
                    <p>Failed to fetch tournament details: {(error as Error).message}</p>
                    <Button onClick={() => mutate()}>Retry</Button>
                </div>
            </div>
        )
    }

    if (isLoading || !details) {
        return (
            <div className={pageStyles.pageContainer}>
                <div className={pageStyles.loading}>
                    <h3>Loading tournament details...</h3>
                    <p>Please wait while we fetch the tournament data.</p>
                </div>
            </div>
        )
    }

    const parent = details.parent_competition

    return (
        <>
            {/* Header Buttons */}
            <div className={pageStyles.headerButtons}>
                <Button variant="secondary" onClick={() => setShowSettings(true)}>⚙ Settings</Button>
                <Button onClick={handleCreateEdition} disabled={creatingEdition || !details?.child_competitions.length}>
                    {creatingEdition ? 'Creating...' : '+ New Edition'}
                </Button>
            </div>

            {/* Header */}
            <div className={pageStyles.pageHeader}>
                <div className={styles.titleRow}>
                    <h1>{getParentDisplayName(parent)}</h1>
                    <Button
                        variant="secondary"
                        onClick={() => setShowTournamentImages(true)}
                        className={`${styles.imageEditButton} ${configuredTournamentImageCount === 0 ? styles.imageEditButtonEmpty : ''}`}
                        title="Manage tournament images"
                        aria-label={`Manage tournament images (${configuredTournamentImageCount} of 2 configured)`}
                    >
                        {tournamentLogoUrl && !tournamentLogoLoadFailed ? (
                            <img
                                className={styles.imageButtonLogo}
                                src={tournamentLogoUrl}
                                alt=""
                                aria-hidden="true"
                                onError={() => setTournamentLogoLoadFailed(true)}
                            />
                        ) : (
                            <svg
                                className={styles.imageButtonIcon}
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <rect x="3" y="5" width="18" height="14" rx="2" />
                                <circle cx="9" cy="10" r="1.5" />
                                <path d="m21 16-5.5-5.5L8 18" />
                                <path d="m13.5 13.5 1.5-1.5 3 3" />
                            </svg>
                        )}
                        <span className={styles.imageButtonCount}>({configuredTournamentImageCount})</span>
                    </Button>
                </div>
                {(() => {
                    const primaryName = getParentDisplayName(parent)
                    const secondaryName = getParentSecondaryName(parent)
                    return secondaryName && secondaryName !== primaryName ? (
                        <p className={styles.titleSubtitle}>{secondaryName}</p>
                    ) : null
                })()}
                <p>
                    <code className="id-code">{parent.ext_competition_parent_id}</code>
                    {' '}
                    {parent.sport_name && <Badge variant="primary">{parent.sport_name}</Badge>}
                    {' '}
                    {parent.catalog_source_name && <Badge variant="secondary">{parent.catalog_source_name}</Badge>}
                    {' · '}
                    <span className={styles.competitionsSummary}>
                        {details.child_competitions.length} competition{details.child_competitions.length !== 1 ? 's' : ''}
                    </span>
                </p>
            </div>

            {/* Action Error Banner */}
            {actionError && (
                <Banner
                    variant="error"
                    message={actionError}
                    onDismiss={() => setActionError(null)}
                    autoScroll
                />
            )}

            {/* Editions List */}
            <div className={styles.editionsList}>
                {editions.length === 0 ? (
                    <div className={listTableStyles.noResults}>
                        <p>No editions found for this tournament.</p>
                        <Button onClick={handleCreateEdition} disabled={creatingEdition}>
                            {creatingEdition ? 'Creating...' : 'Create First Edition'}
                        </Button>
                    </div>
                ) : (
                    editions.map(edition => {
                        const editionHasChanges = hasEditionChanges(edition)
                        return (
                            <div key={edition.year}>
                                {/* Edition title row */}
                                <div className={listTableStyles.tableTitle}>
                                    <h3>{edition.year} Edition</h3>
                                    {editionHasChanges && (
                                        <div className={listTableStyles.tableTitleActions}>
                                            <Button
                                                onClick={() => handleSaveEdition(edition)}
                                                size="small"
                                                disabled={savingSeasons.size > 0}
                                            >
                                                {savingSeasons.size > 0 ? 'Saving...' : 'Save'}
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={() => handleRevertEdition(edition)}
                                                size="small"
                                            >
                                                Revert
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {/* Column headers */}
                                <div className={`${listTableStyles.tableHeader} ${styles.competitionsGrid}`}>
                                    <div>Competition</div>
                                    <div>Season & Complex</div>
                                    <div>Dates</div>
                                    <div className={styles.licensedHeader}>
                                        Lic.
                                        <input
                                            type="checkbox"
                                            checked={isEditionFullyLicensed(edition)}
                                            ref={(el) => {
                                                if (el) el.indeterminate = isEditionPartiallyLicensed(edition)
                                            }}
                                            onChange={(e) => handleToggleEditionLicense(edition, e.target.checked)}
                                            disabled={updatingLicense.size > 0}
                                            title={isEditionFullyLicensed(edition) ? 'Unlicense all' : 'License all'}
                                        />
                                    </div>
                                    <div>Background Image</div>
                                    <div></div>
                                </div>
                                <div className={listTableStyles.tableBody}>
                                    {edition.competitions.map(({ competition, season }) => {
                                        const edits = season ? seasonEdits[season.tc_season_id] : null
                                        const isSaving = season ? savingSeasons.has(season.tc_season_id) : false
                                        const isAddingSeason = addingSeasons.has(`${competition.tc_competition_id}-${edition.year}`)

                                        return (
                                            <div
                                                key={competition.tc_competition_id}
                                                className={`${listTableStyles.tableRow} ${styles.competitionsGrid}`}
                                            >
                                                <div className={listTableStyles.cellPrimary}>
                                                    <p className={listTableStyles.primaryText}>{getCompetitionLabel(competition)}</p>
                                                    {competition.category && (
                                                        <Badge variant="neutral">{competition.category}</Badge>
                                                    )}
                                                </div>

                                                <div className={`${styles.stackedInputs} ${styles.seasonComplexCell}`}>
                                                    {season && edits ? (
                                                        <>
                                                            <div>
                                                                <label>Season Name</label>
                                                                <OverridableField
                                                                    compact
                                                                    tcValue={edits.name}
                                                                    extValue={season.ext_name}
                                                                    onRevert={() => handleRevertSeasonName(season)}
                                                                    modifiedClassName="modified"
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        value={edits.name}
                                                                        onChange={(e) => handleSeasonNameChange(season.tc_season_id, e.target.value)}
                                                                        disabled={isSaving}
                                                                        placeholder="Season name"
                                                                    />
                                                                </OverridableField>
                                                            </div>
                                                            <div className={!edits.complex ? 'fieldIncomplete' : ''}>
                                                                <label>Complex</label>
                                                                <OverridableField
                                                                    compact
                                                                    tcValue={edits.complex?.tc_id || null}
                                                                    extValue={season.ext_complex?.tc_complex_id || null}
                                                                    extDisplayValue={season.ext_complex?.tc_name || season.ext_complex?.ext_name}
                                                                    onRevert={() => handleRevertComplex(season)}
                                                                >
                                                                    <ComplexSelect
                                                                        value={edits.complex}
                                                                        onChange={(complex) => handleSeasonComplexChange(season.tc_season_id, complex)}
                                                                        placeholder="Select complex..."
                                                                        isDisabled={isSaving}
                                                                    />
                                                                </OverridableField>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            variant="secondary"
                                                            size="small"
                                                            onClick={() => handleAddSeasonForCompetition(competition, edition.year)}
                                                            disabled={isAddingSeason}
                                                        >
                                                            {isAddingSeason ? 'Adding...' : '+ Add Season'}
                                                        </Button>
                                                    )}
                                                </div>

                                                {season && edits ? (
                                                    <>
                                                        <div className={styles.stackedInputs}>
                                                            <div className={!edits.start ? 'fieldIncomplete' : ''}>
                                                                <label>Start</label>
                                                                <OverridableField
                                                                    compact
                                                                    tcValue={edits.start}
                                                                    extValue={formatDateForInput(season.ext_start_date)}
                                                                    onRevert={() => handleRevertStartDate(season)}
                                                                    modifiedClassName="modified"
                                                                >
                                                                    <input
                                                                        type="date"
                                                                        value={edits.start}
                                                                        onChange={(e) => handleSeasonDateChange(season.tc_season_id, 'start', e.target.value)}
                                                                        disabled={isSaving}
                                                                    />
                                                                </OverridableField>
                                                            </div>
                                                            <div className={!edits.end ? 'fieldIncomplete' : ''}>
                                                                <label>End</label>
                                                                <OverridableField
                                                                    compact
                                                                    tcValue={edits.end}
                                                                    extValue={formatDateForInput(season.ext_end_date)}
                                                                    onRevert={() => handleRevertEndDate(season)}
                                                                    modifiedClassName="modified"
                                                                >
                                                                    <input
                                                                        type="date"
                                                                        value={edits.end}
                                                                        onChange={(e) => handleSeasonDateChange(season.tc_season_id, 'end', e.target.value)}
                                                                        disabled={isSaving}
                                                                    />
                                                                </OverridableField>
                                                            </div>
                                                        </div>
                                                        <div className={styles.licensedCell}>
                                                            <input
                                                                type="checkbox"
                                                                checked={season.is_licensed}
                                                                onChange={() => handleToggleSeasonLicense(season)}
                                                                disabled={updatingLicense.has(season.tc_season_id)}
                                                                title={season.is_licensed ? 'Licensed - click to remove' : 'Not licensed - click to add'}
                                                            />
                                                        </div>
                                                        <div className={styles.backgroundCell}>
                                                            <ImageSlot
                                                                image={season.background_image ? {
                                                                    url: season.background_image.cdn_url,
                                                                    width: season.background_image.pixel_width,
                                                                    height: season.background_image.pixel_height,
                                                                } : null}
                                                                aspectRatio={`${SEASON_BACKGROUND_REQUIRED_WIDTH} / ${SEASON_BACKGROUND_REQUIRED_HEIGHT}`}
                                                                onUpload={(file) => handleUploadSeasonBackground(season, file)}
                                                                onRemove={season.background_image ? () => handleDeleteSeasonBackground(season) : undefined}
                                                            />
                                                        </div>
                                                        <div className={styles.deleteCell}>
                                                            {!season.ext_season_id && (
                                                                <Button
                                                                    variant="secondary"
                                                                    size="small"
                                                                    onClick={() => handleDeleteSeason(
                                                                        season.tc_season_id,
                                                                        edits.name || 'this season'
                                                                    )}
                                                                    title="Delete season"
                                                                >
                                                                    ✕
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className={styles.noSeason}>—</div>
                                                        <div className={styles.noSeason}>—</div>
                                                        <div className={styles.noSeason}>—</div>
                                                        <div></div>
                                                    </>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    })
                )}
            </div>

            {tournamentId && (
                <Modal
                    isOpen={showTournamentImages}
                    onClose={() => {
                        setShowTournamentImages(false)
                        void mutateTournamentImages()
                    }}
                    title="Tournament Images"
                    size="large"
                >
                    <ImageManagement entityType="tournament" entityId={tournamentId} />
                </Modal>
            )}

            {/* Settings Modal */}
            <Modal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                title="Tournament Settings"
                size="large"
            >
                {/* Parent Tournament Name */}
                <div className={formStyles.formSection}>
                    <h3>Tournament Name</h3>
                    <div className={styles.fieldRow}>
                        <div className={formStyles.formField}>
                            <OverridableField
                                label="Official Name"
                                tcValue={parentOfficialName}
                                extValue={parent.ext_official_name}
                                onRevert={() => setParentOfficialName(parent.ext_official_name || '')}
                            >
                                <input
                                    type="text"
                                    value={parentOfficialName}
                                    onChange={(e) => setParentOfficialName(e.target.value)}
                                    className={parentOfficialName !== (parent.ext_official_name || '') ? 'modified' : ''}
                                    placeholder="e.g. Roland Garros, Mutua Madrid Open"
                                />
                            </OverridableField>
                        </div>
                        <div className={formStyles.formField}>
                            <OverridableField
                                label="Name"
                                tcValue={parentName}
                                extValue={parent.ext_name}
                                onRevert={() => setParentName(parent.ext_name || '')}
                            >
                                <input
                                    type="text"
                                    value={parentName}
                                    onChange={(e) => setParentName(e.target.value)}
                                    className={parentName !== (parent.ext_name || '') ? 'modified' : ''}
                                    placeholder="Tournament name"
                                />
                            </OverridableField>
                        </div>
                    </div>
                </div>

                {/* Location */}
                <div className={formStyles.formSection}>
                    <h3>Location</h3>
                    <div className={styles.fieldRow}>
                        <div className={formStyles.formField}>
                            <OverridableField
                                label="City"
                                tcValue={parentLatestCity}
                                extValue={parent.ext_latest_city}
                                onRevert={() => setParentLatestCity(parent.ext_latest_city || '')}
                            >
                                <input
                                    type="text"
                                    value={parentLatestCity}
                                    onChange={(e) => setParentLatestCity(e.target.value)}
                                    className={parentLatestCity !== (parent.ext_latest_city || '') ? 'modified' : ''}
                                    placeholder="e.g. Paris"
                                />
                            </OverridableField>
                        </div>
                        <div className={formStyles.formField}>
                            <OverridableField
                                label="Country"
                                tcValue={parentLatestCountryCode || null}
                                extValue={parent.ext_latest_country?.code ?? null}
                                extDisplayValue={parent.ext_latest_country?.name ?? parent.ext_latest_country?.code ?? null}
                                onRevert={() => setParentLatestCountryCode(parent.ext_latest_country?.code || '')}
                            >
                                <Select
                                    options={countryOptions}
                                    value={countryOptions.find(o => o.value === parentLatestCountryCode) || null}
                                    onChange={option => setParentLatestCountryCode(option?.value || '')}
                                    isClearable
                                    placeholder="Select country..."
                                    className={`${selectStyles.reactSelect} ${parentLatestCountryCode !== (parent.ext_latest_country?.code || '') ? selectStyles.modified : ''}`}
                                    classNamePrefix="react-select"
                                />
                            </OverridableField>
                        </div>
                        <div className={formStyles.formField}>
                            <OverridableField
                                label="Timezone"
                                tcValue={parentLatestTimezone || null}
                                extValue={parent.ext_latest_timezone}
                                onRevert={() => setParentLatestTimezone(parent.ext_latest_timezone || '')}
                            >
                                <Select
                                    options={TIMEZONE_OPTIONS}
                                    value={TIMEZONE_OPTIONS.find(o => o.value === parentLatestTimezone) || (parentLatestTimezone ? { value: parentLatestTimezone, label: parentLatestTimezone } : null)}
                                    onChange={option => setParentLatestTimezone(option?.value || '')}
                                    isClearable
                                    placeholder="Select timezone..."
                                    className={`${selectStyles.reactSelect} ${parentLatestTimezone !== (parent.ext_latest_timezone || '') ? selectStyles.modified : ''}`}
                                    classNamePrefix="react-select"
                                />
                            </OverridableField>
                        </div>
                    </div>
                </div>

                {/* URL Slug */}
                <div className={formStyles.formSection}>
                    <SlugManager
                        entityType="competition_parent"
                        entityId={parent.tc_competition_parent_id}
                        embedded
                    />
                </div>

                {/* Competitions */}
                <div className={formStyles.formSection}>
                    <CompetitionsEditor
                        competitions={competitionItems}
                        onChange={setCompetitionItems}
                        onDelete={handleDeleteCompetition}
                        showHeader={true}
                    />
                </div>

                {/* Save button for all settings */}
                <div className={formStyles.formFooter}>
                    <Button
                        onClick={handleSaveAllSettings}
                        disabled={!hasAnySettingsChanges || savingAllCompetitions}
                    >
                        {savingAllCompetitions ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </Modal>

        </>
    )
}

export default TournamentDetail
